/**
 * Extended FHIR Resources Dashboard
 * Simple dashboard to test and display the new FHIR resource types
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Stethoscope,
  Pill,
  UserCheck,
  Scissors,
  Users,
  Flag,
  Download,
  RefreshCw,
  Activity,
  AlertCircle,
} from "lucide-react";
import { fhirApi } from "@/services/api/fhirApi";
import { FHIRBundle } from "@/types/fhir";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { log } from "@/lib/log";
import { useI18nStore } from "@/stores/i18nStore";

interface ResourceStats {
  diagnosticReports: number;
  medicationRequests: number;
  encounters: number;
  procedures: number;
  relatedPersons: number;
  flags: number;
  total: number;
}

const ExtendedFHIRDashboard: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  // Fetch extended FHIR resources
  const {
    data: diagnosticReports,
    isLoading: diagnosticLoading,
    refetch: refetchDiagnostics,
  } = useQuery({
    queryKey: ["fhir-diagnostic-reports"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["DiagnosticReport"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: medicationRequests,
    isLoading: medicationLoading,
    refetch: refetchMedications,
  } = useQuery({
    queryKey: ["fhir-medication-requests"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["MedicationRequest"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: encounters,
    isLoading: encounterLoading,
    refetch: refetchEncounters,
  } = useQuery({
    queryKey: ["fhir-encounters"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["Encounter"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: procedures,
    isLoading: procedureLoading,
    refetch: refetchProcedures,
  } = useQuery({
    queryKey: ["fhir-procedures"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["Procedure"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: relatedPersons,
    isLoading: relatedLoading,
    refetch: refetchRelated,
  } = useQuery({
    queryKey: ["fhir-related-persons"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["RelatedPerson"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: flags,
    isLoading: flagLoading,
    refetch: refetchFlags,
  } = useQuery({
    queryKey: ["fhir-flags"],
    queryFn: async () => {
      const blob = await fhirApi.exportData({
        resourceTypes: ["Flag"],
        includeMetadata: false,
      });
      const text = await blob.text();
      return JSON.parse(text) as FHIRBundle;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Calculate statistics
  const stats: ResourceStats = {
    diagnosticReports: diagnosticReports?.total || 0,
    medicationRequests: medicationRequests?.total || 0,
    encounters: encounters?.total || 0,
    procedures: procedures?.total || 0,
    relatedPersons: relatedPersons?.total || 0,
    flags: flags?.total || 0,
    total:
      (diagnosticReports?.total || 0) +
      (medicationRequests?.total || 0) +
      (encounters?.total || 0) +
      (procedures?.total || 0) +
      (relatedPersons?.total || 0) +
      (flags?.total || 0),
  };

  const isLoading =
    diagnosticLoading ||
    medicationLoading ||
    encounterLoading ||
    procedureLoading ||
    relatedLoading ||
    flagLoading;

  const handleRefreshAll = () => {
    refetchDiagnostics();
    refetchMedications();
    refetchEncounters();
    refetchProcedures();
    refetchRelated();
    refetchFlags();
  };

  const handleExportAll = async () => {
    try {
      const blob = await fhirApi.exportData({
        resourceTypes: [
          "DiagnosticReport",
          "MedicationRequest",
          "Encounter",
          "Procedure",
          "RelatedPerson",
          "Flag",
        ],
        includeMetadata: true,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `extended-fhir-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      log.debug("✅ Extended FHIR export successful");
    } catch (error: any) {
      console.error("❌ Extended FHIR export failed:", error);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t.fhirxExtTitle}
          </h1>
          <p className="text-gray-600 mt-2">{t.fhirxExtSubtitle}</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleRefreshAll}
            variant="outline"
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            {t.fhirxRefreshAll}
          </Button>
          <Button
            onClick={handleExportAll}
            variant="primary"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t.fhirxExportAll}
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {isLoading ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          <span className="text-blue-800 font-medium">{t.fhirxExtLoading}</span>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Activity className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">
            {t.fhirxExtLoaded.replace("{n}", String(stats.total))}
          </span>
        </div>
      )}

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Total Resources */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">
              {t.fhirxTotalResources}
            </CardTitle>
            <Activity className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900">
              {stats.total}
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {t.fhirxExtAllResources}
            </p>
          </CardContent>
        </Card>

        {/* Diagnostic Reports */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxDiagnosticReports}
            </CardTitle>
            <Stethoscope className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.diagnosticReports}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxDiagnosticReportsHint}
            </p>
          </CardContent>
        </Card>

        {/* Medication Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxMedicationRequests}
            </CardTitle>
            <Pill className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.medicationRequests}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxMedicationRequestsHint}
            </p>
          </CardContent>
        </Card>

        {/* Encounters */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxEncounters}
            </CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.encounters}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxEncountersHint}
            </p>
          </CardContent>
        </Card>

        {/* Procedures */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxProcedures}
            </CardTitle>
            <Scissors className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.procedures}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxProceduresHint}
            </p>
          </CardContent>
        </Card>

        {/* Related Persons */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxRelatedPersons}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.relatedPersons}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxRelatedPersonsHint}
            </p>
          </CardContent>
        </Card>

        {/* Flags */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxHealthFlags}
            </CardTitle>
            <Flag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.flags}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t.fhirxFlagsHint}
            </p>
          </CardContent>
        </Card>

        {/* Test Status */}
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-orange-800">
              {t.fhirxTestStatus}
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-900">✓</div>
            <p className="text-xs text-orange-600 mt-1">
              {t.fhirxAllTypesImplemented}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Resource Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Implementation Status */}
        <Card>
          <CardHeader>
            <CardTitle>{t.fhirxImplementationStatus}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">DiagnosticReport</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">MedicationRequest</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Encounter</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Procedure</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">RelatedPerson</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Flag</span>
              <span className="text-sm font-medium text-green-600">
                ✓ {t.fhirxImplemented}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Export Options */}
        <Card>
          <CardHeader>
            <CardTitle>{t.fhirxExportOptions}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-gray-600">{t.fhirxExportIntro}</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• {t.fhirxExportItemReports}</li>
                <li>• {t.fhirxExportItemMeds}</li>
                <li>• {t.fhirxExportItemEncounters}</li>
                <li>• {t.fhirxExportItemProcedures}</li>
                <li>• {t.fhirxExportItemRelated}</li>
                <li>• {t.fhirxExportItemFlags}</li>
              </ul>
            </div>
            <Button
              onClick={handleExportAll}
              variant="outline"
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {t.fhirxDownloadBundle}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Debug Information */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="text-lg">{t.fhirxDebugInfo}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium mb-2">{t.fhirxBundleTotals}</p>
              <ul className="space-y-1 text-gray-600">
                <li>DiagnosticReport: {diagnosticReports?.total || 0}</li>
                <li>MedicationRequest: {medicationRequests?.total || 0}</li>
                <li>Encounter: {encounters?.total || 0}</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">{t.fhirxAdditionalResources}</p>
              <ul className="space-y-1 text-gray-600">
                <li>Procedure: {procedures?.total || 0}</li>
                <li>RelatedPerson: {relatedPersons?.total || 0}</li>
                <li>Flag: {flags?.total || 0}</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 p-3 bg-white rounded border">
            <p className="text-xs text-gray-500">
              {t.fhirxLastUpdated.replace("{date}", fmt.dateTime(new Date()))}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExtendedFHIRDashboard;
